/**
 * PhoneInput — supported phone field with country picker.
 *
 * Left: country chip (flag + dial code). It opens CountryCodePicker only
 * when more than one country is actually supported by auth/OTP. This keeps
 * UI promises aligned with backend delivery instead of letting users pick
 * numbers the server will reject.
 * Right: masked national number that auto-formats as the user types
 * (4-digit groups for readability) and accepts up to 15 digits to
 * cover any E.164 national plan.
 *
 * Always emits clean E.164 (`+<dial><digits>`) to the parent — or
 * '' when the national number is too short to be valid (< 4 digits)
 * — so Zod / DB validation never has to think about display
 * formatting or country selection.
 *
 * Visually matches every other form field (same border, radius,
 * padding, surfaceSubtle bg, focus state) so the row reads as part of
 * the same design system.
 */

import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { View, Text, TextInput as RNTextInput, Platform, Keyboard } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../lib/hooks/use-theme'
import { useFontScale } from '../../lib/hooks/use-font-scale'
import {
  useSemanticColors,
  SPACING,
  RADIUS,
  FORM_FIELD_GAP,
  INTERACTION,
} from '../../lib/theme/tokens'
import { COLORS, FONTS } from '../../lib/brand'
import { CountryCodePicker } from './CountryCodePicker'
import {
  DEFAULT_COUNTRY_ISO2,
  findCountry,
  supportedPhoneCountries,
  SUPPORTED_PHONE_COUNTRY_ISO2S,
  type Country,
} from '../../lib/data/countries'
import { isOtpCapablePhone } from '../../lib/validations/auth.schema'

const NATIONAL_DIGITS_MAX = 15 // E.164 max national-number length
const NATIONAL_DIGITS_MIN = 4 // anything shorter is clearly incomplete
const SEPARATOR_INTERVAL = 4 // group digits in 4s for readability

interface PhoneInputProps {
  /** E.164: '+<dial><digits>' or '' if not yet a complete valid number. */
  value: string
  /** Called with E.164 once the national number is at least 4 digits, '' otherwise. */
  onChange: (e164: string) => void
  /**
   * Called on every keystroke with whether SELECTED PREFIX + TYPED DIGITS is a
   * number that can actually receive a verification message.
   *
   * Separate from `onChange` on purpose. `onChange` reports what the person has
   * typed; this reports whether sending to it could succeed. A screen that
   * gates its send button on the emitted string alone would enable it for
   * `+503 2222 3333` — a real, well-formed, land-LINE that no OTP can reach,
   * and one provider credit spent to find that out.
   *
   * `candidate` is that same prefix+digits string, handed over so the screen
   * can say WHY the button is inert. A dead button with no explanation is its
   * own bug: the person cannot tell a wrong digit from a broken app.
   */
  onValidityChange?: (isValid: boolean, candidate: string) => void
  label?: string
  error?: string
  helperText?: string
  testID?: string
  supportedCountryIso2s?: readonly string[]
}

const onlyDigits = (s: string) => s.replace(/\D/g, '')

function formatNational(digits: string): string {
  const limited = digits.slice(0, NATIONAL_DIGITS_MAX)
  const groups: string[] = []
  for (let i = 0; i < limited.length; i += SEPARATOR_INTERVAL) {
    groups.push(limited.slice(i, i + SEPARATOR_INTERVAL))
  }
  return groups.join(' ')
}

/**
 * Best-effort split of an existing E.164 string into (iso2, national digits)
 * by matching the longest dial-code prefix in our country list. Greedy match
 * because dial codes can overlap (e.g. +1 USA vs +1242 Bahamas).
 */
function splitE164(value: string, countries: readonly Country[]): { iso2: string; digits: string } {
  // The configured default first, and only then the head of the list.
  //
  // This read `countries[0]?.iso2 ?? DEFAULT_COUNTRY_ISO2`, where the `??` can
  // only fire when the list is EMPTY — so the default was unreachable and the
  // first entry always won. The list is the seven Central American countries
  // sorted by name in Spanish, and the first of those is Belice: every person
  // opening the phone field in a Salvadoran app was handed +501.
  //
  // An account on this platform exists only once a phone is verified, so a
  // country code nobody noticed was wrong is not a cosmetic default — it is an
  // OTP sent to another country that never arrives.
  const fallbackIso2 =
    countries.find((c) => c.iso2 === DEFAULT_COUNTRY_ISO2)?.iso2 ??
    countries[0]?.iso2 ??
    DEFAULT_COUNTRY_ISO2
  if (!value || !value.startsWith('+')) {
    return { iso2: fallbackIso2, digits: onlyDigits(value).slice(0, NATIONAL_DIGITS_MAX) }
  }
  const stripped = onlyDigits(value)
  // Try the longest dial codes first for accurate matching.
  const sorted = [...countries].sort((a, b) => b.dialCode.length - a.dialCode.length)
  for (const c of sorted) {
    if (stripped.startsWith(c.dialCode)) {
      return {
        iso2: c.iso2,
        digits: stripped.slice(c.dialCode.length).slice(0, NATIONAL_DIGITS_MAX),
      }
    }
  }
  return { iso2: fallbackIso2, digits: stripped.slice(0, NATIONAL_DIGITS_MAX) }
}

export const PhoneInput = forwardRef<RNTextInput, PhoneInputProps>(function PhoneInput(
  {
    value,
    onChange,
    onValidityChange,
    label,
    error,
    helperText,
    testID,
    supportedCountryIso2s = SUPPORTED_PHONE_COUNTRY_ISO2S,
  },
  ref,
) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  // Same contract as TextInput: the field floor moves with its own text.
  const { box } = useFontScale()
  const [focused, setFocused] = useState(false)
  const allowedCountries = useMemo(
    () => supportedPhoneCountries(supportedCountryIso2s),
    [supportedCountryIso2s],
  )

  // Seed local state from the parent value once. Subsequent prop changes
  // do NOT stomp in-progress typing (one-way: child → parent).
  const initial = useMemo(() => splitE164(value, allowedCountries), [value, allowedCountries])
  const [iso2, setIso2] = useState<string>(initial.iso2)
  const [display, setDisplay] = useState<string>(formatNational(initial.digits))

  const country =
    allowedCountries.find((item) => item.iso2 === iso2) ??
    // Same ordering as splitE164 above: the configured default outranks
    // whichever country happens to sort first.
    allowedCountries.find((item) => item.iso2 === DEFAULT_COUNTRY_ISO2) ??
    allowedCountries[0] ??
    findCountry(DEFAULT_COUNTRY_ISO2)!

  // Re-emit when the parent clears the field (e.g. form reset).
  useEffect(() => {
    if (!value) setDisplay('')
  }, [value])

  // Seed validity from a PREFILLED number, once, on mount.
  //
  // Validity is otherwise only reported from `emit`, which runs on typing and
  // on changing country — neither of which happens when the screen arrives with
  // the number already in hand (the phone-in-use recovery handoff re-opens this
  // field pre-filled). Without this, a complete, valid, recovered number left
  // the send button greyed out with nothing on screen explaining why, and the
  // only way forward was to delete a digit and type it again.
  useEffect(() => {
    onValidityChange?.(isOtpCapablePhone(value), value)
    // Mount only: afterwards `emit` owns this, and re-running on every parent
    // re-render would fight in-progress typing (the parent's `value` lags the
    // field by one keystroke for numbers under 4 digits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const emit = useCallback(
    (digits: string, dialCode: string) => {
      // The candidate is ALWAYS prefix + digits, even mid-typing. That string —
      // not the '' this component emits for a half-typed number — is what the
      // numbering-plan check has to see, so the send button lights up on the
      // exact keystroke that completes a real number and not one before.
      const candidate = `+${dialCode}${digits}`
      onChange(digits.length >= NATIONAL_DIGITS_MIN ? candidate : '')
      onValidityChange?.(digits.length > 0 && isOtpCapablePhone(candidate), candidate)
    },
    [onChange, onValidityChange],
  )

  const handleChangeText = useCallback(
    (raw: string) => {
      // People type and paste their number with the country code in front,
      // next to a field that already shows +503. Before this, that produced
      // "503 7687 0740" and the form answered "Usá un número salvadoreño de 8
      // dígitos" — technically true and completely unhelpful, since the number
      // they entered was correct.
      //
      // Only strip it when what remains still looks like a national number, so
      // a real number that happens to start with the dial code (Salvadoran
      // mobiles are 8 digits and never start with 503, but this component is
      // shared) is not silently truncated.
      const entered = onlyDigits(raw)
      const dial = onlyDigits(country.dialCode)
      const withoutDial =
        dial && entered.startsWith(dial) && entered.length > dial.length + NATIONAL_DIGITS_MIN
          ? entered.slice(dial.length)
          : entered

      const digits = withoutDial.slice(0, NATIONAL_DIGITS_MAX)
      setDisplay(formatNational(digits))
      emit(digits, country.dialCode)
    },
    [emit, country.dialCode],
  )

  const handleCountryChange = useCallback(
    (newIso2: string) => {
      setIso2(newIso2)
      const newCountry = findCountry(newIso2)
      if (!newCountry) return
      const digits = onlyDigits(display).slice(0, NATIONAL_DIGITS_MAX)
      emit(digits, newCountry.dialCode)
      // After picking a country, keep focus off the field so the user sees
      // the new flag/dial code before typing — not a keyboard covering it.
      Keyboard.dismiss()
    },
    [display, emit],
  )

  const handleFocus = useCallback(() => setFocused(true), [])
  const handleBlur = useCallback(() => setFocused(false), [])

  const borderColor = error
    ? COLORS.coral.DEFAULT
    : focused
      ? isDark
        ? COLORS.coral.DEFAULT
        : COLORS.purple.light
      : colors.border

  return (
    <View style={{ marginBottom: FORM_FIELD_GAP }}>
      {label ? (
        <Text
          style={{
            fontSize: 14,
            fontFamily: FONTS.body,
            letterSpacing: 0.2,
            // 10 → 16px, matches TextInput / DatePicker / Select label gap.
            marginBottom: SPACING.md,
            color: colors.textPrimary,
            opacity: 0.75,
          }}
        >
          {label}
        </Text>
      ) : null}

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'stretch',
          minHeight: box(INTERACTION.fieldHeight),
          borderRadius: RADIUS.md,
          borderWidth: 1,
          borderColor,
          backgroundColor: colors.surfaceSubtle,
          overflow: 'hidden',
        }}
      >
        <CountryCodePicker
          value={iso2}
          onChange={handleCountryChange}
          error={!!error}
          countries={allowedCountries}
          testID={testID ? `${testID}.country` : 'phone.country'}
        />

        <RNTextInput
          ref={ref}
          value={display}
          onChangeText={handleChangeText}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={
            country.dialCode === '1'
              ? '555 123 4567'
              : country.dialCode === '52'
                ? '55 1234 5678'
                : '7777 7777'
          }
          placeholderTextColor={colors.textMuted}
          keyboardType="phone-pad"
          inputMode="tel"
          maxLength={NATIONAL_DIGITS_MAX + 4} // digits + up to 4 separator spaces
          autoComplete="tel-national"
          textContentType="telephoneNumber"
          style={[
            {
              flex: 1,
              paddingHorizontal: SPACING.md,
              paddingVertical: SPACING.md,
              fontFamily: FONTS.body,
              fontSize: 15,
              color: colors.textPrimary,
            },
            Platform.OS === 'web' ? ({ outlineStyle: 'none' } as Record<string, string>) : null,
          ]}
          testID={testID}
          accessibilityLabel={label ?? t('auth.phone')}
        />
      </View>

      {error ? (
        <Text
          style={{
            fontSize: 12,
            color: colors.textAccent,
            fontFamily: FONTS.body,
            marginTop: SPACING.xs,
          }}
        >
          {error}
        </Text>
      ) : helperText ? (
        <Text
          style={{
            fontSize: 12,
            color: colors.textMuted,
            fontFamily: FONTS.body,
            marginTop: SPACING.xs,
          }}
        >
          {helperText}
        </Text>
      ) : null}
    </View>
  )
})
